import { colorValue, List } from '@hai/ui-react';
import { isEmpty, isNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import BusyIcon from 'src/js/component/base/busy-icon';
import { triggerEvent, watchEvent } from 'src/js/events';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { scrollPanelIntoView } from 'src/js/component/actionBar/streaming-action-bar';
import { constant } from 'src/js/constant';
import {
  St2110ViewModel,
  st2110IsStopped,
  st2110StateToColor,
  st2110StateToIcon,
  st2110TypeToIcon,
  st2110TypeToNoName,
} from './st2110-models';
import { St2110Edit } from './st2110-edit';
import { St2110Statistics } from './st2110-statistics';
import { startStopSt2110 } from './st2110-store';
import { NmosServiceActiveContext } from './st2110';
import { PushButton } from 'src/js/component/base/push-button';

interface St2110ItemProps {
  model: St2110ViewModel;
  onSelect: any;
  scrollToId?: string | number;
}

export const St2110Row = (props: St2110ItemProps) => {
  const { model, onSelect, scrollToId } = props;

  const { t } = useTranslation();
  const { sessionStore } = useStores();
  const location = useLocation();
  const showStats = location?.state?.stats === true;
  const newSt2110 = model.id === -1;
  const scroll = location?.state?.id === model.id || scrollToId === model.id;

  const [isExpanded, setIsExpanded] = useState<boolean>(scroll);
  const [collapseTimeoutId, setCollapseTimeoutId] = useMutable(null);
  const [expandContent, setExpandContent] = useState<string>(scroll ? (showStats ? 'STATISTICS' : 'SETTINGS') : null);
  const onExpandedStateChange = (expandedState: { eventKey: any; expanded?: boolean }) => {
    if (isExpanded !== expandedState.expanded && expandedState.expanded != null) {
      setIsExpanded(expandedState.expanded);
    }
  };

  watchEvent('expandAll:edit', () => {
    setExpandContent('SETTINGS');
    setIsExpanded(true);
  });
  watchEvent('expandAll:stats', () => {
    setExpandContent('STATISTICS');
    setIsExpanded(true);
  });
  const collapseAll = () => {
    setIsExpanded(false);
    setCollapseTimeoutId(
      setTimeout(() => {
        // the component needs to stay rendered during close for smooth closing
        setExpandContent(null);
        setCollapseTimeoutId(null);
      }, constant.collapseTime),
    );
  };
  watchEvent('collapseAll:edit', collapseAll);
  watchEvent('collapseAll:stats', collapseAll);

  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const onSelectHandler = (eventKey: string, expanded?: boolean, ev?: React.KeyboardEvent | React.MouseEvent) => {
    switch (eventKey) {
      case 'STATISTICS':
      case 'SETTINGS':
        if (ev && ev.shiftKey) {
          // expend all
          if (expanded) {
            eventKey === 'SETTINGS' ? triggerEvent('expandAll:edit') : triggerEvent('expandAll:stats');
          } else {
            eventKey === 'SETTINGS' ? triggerEvent('collapseAll:edit') : triggerEvent('collapseAll:stats');
          }
          return;
        }
        setExpandContent(eventKey);
        if (expanded) {
          setIsExpanded(true);
          if (collapseTimeoutId() !== null) {
            clearTimeout(collapseTimeoutId());
            setCollapseTimeoutId(null); // prevent closing of panel
          }
          scroll && scrollPanelIntoView({ id: model.id });
        } else {
          setIsExpanded(false);
          setCollapseTimeoutId(
            setTimeout(() => {
              // the component needs to stay rendered during close for smooth closing
              setExpandContent(null);
              setCollapseTimeoutId(null);
            }, constant.collapseTime),
          );
        }
        break;
      case 'PLAY':
        setIsUpdating(true);
        startStopSt2110(model, st2110IsStopped(model.state), false, true, t).then(() => {
          triggerEvent('preset-update');
          setIsUpdating(false);
        });
        break;
      default:
        break;
    }
  };
  useEffect(() => {
    return () => {
      // final cleanup
      if (collapseTimeoutId() !== null) {
        clearTimeout(collapseTimeoutId());
      }
    };
  }, []);

  const btnId = `st2110-settings-${model.id}`;
  const handleCancelEdit = () => {
    const btn = document.getElementById(btnId);
    if (!isNil(btn)) {
      btn.click();
    }
  };

  const onClickListPanel = (ev: React.MouseEvent) => {
    ev?.stopPropagation();
    ev?.preventDefault();
    if (
      (ev.target as any)?.className !== undefined /*
      (ev.target as any)?.className?.includes?.('HaiListPanelTitle') ||
      (ev.target as any)?.className?.includes?.('list-panel-main-content')*/
    ) {
      if (expandContent == null) {
        setExpandContent('SETTINGS');
      }
      // so we dont expand empty content
      setIsExpanded(!isExpanded);
    }
  };

  const statusColor = st2110StateToColor(model.state, model.stats.streamSync);
  //const input = !isNilOrEmpty(videoInputs) && videoInputs.find((i) => i.id === model.inputChannel);
  const nmosServiceActive = React.useContext(NmosServiceActiveContext);
  return (
    <>
      <div id={`panel-${model.id}`} className={model.hidden ? 'mk-row-hidden' : ''} style={{ scrollMarginTop: '21px' }}>
        <List.Panel
          onClick={onClickListPanel}
          checked={model.selected}
          onSelect={(selected: boolean) => onSelect(model, selected)}
          panelColor={statusColor}
          disabled={newSt2110}
          expandedState={{ eventKey: expandContent, expanded: isExpanded }}
          onExpandedStateChange={onExpandedStateChange}
        >
          {!newSt2110 && (
            <List.PanelTitle
              {...{ onClick: onClickListPanel }}
              useFsMask={true}
              title={
                !isEmpty(model.name)
                  ? model.name
                  : t('st2110.unnamedSt2110', { type: st2110TypeToNoName(model.type, t), output: model.output })
              }
              statusIcon={st2110StateToIcon(model.state, model.stats.streamSync)}
              iconColor={statusColor}
            />
          )}
          {!newSt2110 && (
            <List.PanelDetail>
              <List.PanelDetailItem divider="vertical" text={t('general.id')}>
                {/*<Chip size="sm-square">{model.id.toString()}</Chip>*/}
                <PushButton className="small">{model.id.toString()}</PushButton>
              </List.PanelDetailItem>
              <List.PanelDetailItem divider="vertical" text={model.typeTxt} icon={st2110TypeToIcon(model.type)} />
              <List.PanelDetailItem divider="vertical" text={model.addressTxt} />
              <List.PanelDetailItem text={`${t('st2110.fields.payload')} ${model.rtpPayload.toString()}`} />
            </List.PanelDetail>
          )}
          {!newSt2110 && sessionStore.isUser() && (
            <List.Actions>
              <List.ActionItem
                expandButton
                eventKey="STATISTICS"
                icon="ReportStats"
                title={t('general.statistics')}
                onSelect={onSelectHandler}
              />
              <List.ActionItem
                expandButton
                eventKey="SETTINGS"
                icon="Settings"
                title={t('general.settings')}
                onSelect={onSelectHandler}
                id={btnId}
              />
            </List.Actions>
          )}
          {!newSt2110 && !sessionStore.isUser() && (
            <List.Actions>
              <List.ActionItem
                expandButton
                eventKey="STATISTICS"
                icon="ReportStats"
                title={t('general.statistics')}
                onSelect={onSelectHandler}
              />
              <List.ActionItem
                expandButton
                eventKey="SETTINGS"
                icon="Settings"
                title={t('general.settings')}
                onSelect={onSelectHandler}
                id={btnId}
              />
              <List.ActionItem isDivider />
              <List.ActionItem
                eventKey="PLAY"
                icon={
                  <BusyIcon
                    isActive={!st2110IsStopped(model.state)}
                    activeIcon="StopFilled"
                    inactiveIcon="PlayFilled"
                    busy={isUpdating}
                    color={colorValue('haiui-gray-04')}
                  />
                }
                showActive={false}
                onSelect={onSelectHandler}
                disabled={isUpdating || nmosServiceActive}
              />
            </List.Actions>
          )}
          <List.ExpandedPanel>
            {expandContent === 'STATISTICS' && isExpanded && <St2110Statistics model={model} />}
            {expandContent === 'SETTINGS' && isExpanded && (
              <St2110Edit model={model} onCancel={handleCancelEdit} nmosServiceActive={nmosServiceActive} />
            )}
          </List.ExpandedPanel>
        </List.Panel>
      </div>
    </>
  );
};
